import { Sprite } from "pixi.js";
import App from "../App";
import GameModel from "../model/GameModel";
import GameLoader from "./GameLoader";
import ConsoleUtil from "./ConsoleUtil";

class FullscreenUtils {
    private inited: boolean = false;

    private header: HTMLDivElement;
    private fullscreenAnimation: HTMLDivElement;
    private viewComponent: HTMLDivElement;
    private overflowDiv: HTMLDivElement;
    
    private handImage: HTMLImageElement;
    private arrowImage: HTMLImageElement;
    private touchstartY = undefined;
    private timer: NodeJS.Timeout;
    private animation: NodeJS.Timeout;

    private barHeightOnTouchDown: number = 0;
    private handYOffset: number = 0;
    private arrowYOffset: number = 0;
    private opacity: number = 0.8;
    private resizeHandler: () => void;

    /**
     * @deprecated Do not use this method anymore, because fullscreen arrow introduces many issues.
     * Even worse, the players can't even start the game!
     *
     */
    public init(resizeHandler: () => void) {
      return;
      this.inited = false;
      let shouldInit = this.isMobile() && !(GameModel.urlParams.fullscreen == "false");
      if (!shouldInit) {
        return;
      }

      // Disable mouse scrolling
      // 2017 recommended event
      App.instance.view.addEventListener("wheel", function (event) { event.preventDefault(); }, false);

      // Before 2017, IE9, Chrome, Safari, Opera
      App.instance.view.addEventListener("mousewheel", function (event) { event.preventDefault(); }, false);

      // Old versions of Firefox
      App.instance.view.addEventListener("DOMMouseScroll", function (event) { event.preventDefault(); }, false);
      
      this.resizeHandler = resizeHandler;
      this.startTimer();
      this.inited = true;
  
      // Disable double tap to zoom on iOS
      document.addEventListener("dblclick", function (e) {
        e.preventDefault();
      });
  
      this.header = document.createElement("div");
      this.header.id = "fullscreenHeader";
      this.header.style.height = "0";
      this.header.style.width = "100%";
      this.header.style.backgroundColor = "red";
  
      this.viewComponent = document.createElement("div");
      this.viewComponent.classList.add("noselect");
      this.viewComponent.id = "fullscreenUnderlay";

      this.fullscreenAnimation = document.createElement("div");
      this.fullscreenAnimation.classList.add("noselect");
      this.fullscreenAnimation.id = "fullscreenAnimation";
      
      this.overflowDiv = document.createElement("div");

      this.overflowDiv.innerText =
        "If you can see this, please tap on screen!";
      document.body.appendChild(this.header);
      document.body.appendChild(this.viewComponent);
      this.viewComponent.appendChild(this.fullscreenAnimation);
      document.body.appendChild(this.overflowDiv);
  
      window.addEventListener("touchstart", (e) => {
        this.touchstartY = undefined;
        this.barHeightOnTouchDown = undefined;
  
        if (e.changedTouches.length == 1) {
          this.stopTimer();
          this.touchstartY = e.changedTouches[0].screenY;
          this.barHeightOnTouchDown = this.computeUrlBarHeight();
        }
      });
  
      window.addEventListener("touchend", (e) => {
        if (this.touchstartY && e.changedTouches.length == 1) {
          let delta = this.touchstartY - e.changedTouches[0].screenY;
          if (delta > 0) {
            if (!this.isFullscreen() && this.barHeightOnTouchDown) {
              let currentBarHeight = this.computeUrlBarHeight();
              let barDeltaSize = this.barHeightOnTouchDown - currentBarHeight;
  
              if (barDeltaSize == 0) {
                // The bar did not change its size, so the swipe will fail!
                // We keep the timer turned off.
                // This way the user must scroll up manually or tap manually,
                // which is exactly what we need :)
                // We will display a message, with instuctions to the user (the overflow div).
                this.touchstartY = undefined;
                return;
              } else if (delta < this.barHeightOnTouchDown) {
                // Swipe too less
                this.hide();
              }
            }
  
            setTimeout(() => {
              this.scroll();
              this.hide();
              setTimeout(() => {
                // Another check!
                if (this.isFullscreen()) {
                  this.hide();
                } else {
                  this.show();
                }
  
                this.startTimer();
              }, 100);
            }, 100);
          } else {
            this.scroll();
            this.startTimer();
          }
        } else {
          this.scroll();
          this.startTimer();
        }
  
        this.touchstartY = undefined;
      });
  
      window.addEventListener("resize", (e) => {
        this.checkForFullscreen();
      });
  
      window.addEventListener("orientationchange", (e) => {
        this.checkForFullscreen();
      });
  
      this.appendHandImages();
      this.checkForFullscreen();
    }
  
    private appendHandImages() {
      if (!this.inited) {
        return;
      }
  
      let arrowSprite = new Sprite(GameLoader.loader.resources.arrow.texture);
      // Migration to PIXI v6 deprecated the extract,
      // We now use the plugings.extract instead.
      let arrowTexture = App.instance.renderer.plugins.extract
          .canvas(arrowSprite)
          .toDataURL("image/png");

      this.arrowImage = document.createElement("img");
      this.arrowImage.src = arrowTexture;
      this.fullscreenAnimation.appendChild(this.arrowImage);

      let handSprite = new Sprite(GameLoader.loader.resources.hand.texture);

      // Migration to PIXI v6 deprecated the extract,
      // We now use the plugings.extract instead.
      let handTexture = App.instance.renderer.plugins.extract.canvas(handSprite).toDataURL("image/png");
      this.handImage = document.createElement("img");
      this.handImage.src = handTexture;
      this.fullscreenAnimation.appendChild(this.handImage);
  
      this.resizeIcons();
    }

    private startTimer() {
      if (!this.timer) {
        this.timer = setInterval(() => {
          this.checkForFullscreen();
        }, 1000);
      }
    }

    private stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = undefined;
      }
    }

    private isMobile() {
      return GameModel.isMobile(true);
    }
  
    private isLandscape() {
      return window.innerWidth > window.innerHeight;
    }
  
    private getScreenHeight() {
      let height;
      if (this.isLandscape()) {
        height = Math.min(window.screen.width, window.screen.height);
      } else {
        height = Math.max(window.screen.width, window.screen.height);
      }
  
      return height;
    }
  
    private isFullscreen() {
      if (GameModel.iOS && GameModel.iOSMajorVersion <= 9) {
        return true;
      }

      let value = window.innerHeight / this.getScreenHeight();
      let barHeight = this.computeUrlBarHeight();
      // // console.log(
      // //   "FullScreenRatio:" + value,
      // //   "Android:" + this.isAndroid(),
      // //   "iOS:" + this.isIOS(),
      // //   "Landscape:" + this.isLandscape()
      // // );
  
      let text = "coef: " +
      value.toFixed(3) +
      " | inner: " +
      window.innerHeight +
      " | client: " +
      document.documentElement.clientHeight +
      " | barHeight: " +
      barHeight;

      let log = document.getElementById("log");
      if (log) {
        document.getElementById("log").innerText = text;
        
      } else {
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.fullscreenIOSFix === true) {
          ConsoleUtil.logWithColor(text, "blue");
        }
      }

  
      // // portrait/landscape screen size 667 no zoom
      // // no zoom portrait c1 = 0.937, c2 = 1.141
      // // no zoom landscape c1 = 1.000, c2 = 1.154
  
      // // zoom portrait/landscape screen size 568
      // // zoom portrait c1 = 0.900, c2 = 1.164
      // // zoom landscape c1 = 1.000, c2 = 1.236
  
      // // no zoom - port inner: 625, client: 548, screen: 667 1.217153284671533
      // // no zoom - land inner: 375, client: 325, screen: 375 1.153846153846154
      // // zoom    - port inner: 511, client: 439, screen: 568 1.293849658314351
      // // zoom    - land inner: 320, client: 259, screen: 320 1.235521235521236
  
      if (document.documentElement.clientHeight != window.innerHeight) {
        // TODO: Chrome on iOS 13 requires >= 0.91 iPhone 6s
        return value >= 0.88;
      } else {
        // Client height and the inner height equals.
        // if (this.isAndroid()) {
        //   return value >= 0.88;
        // }
  
        // iPad has 0.935 and is wrongly considered fullscreen (client = 1010)
        if (GameModel.isIpad) {
          return value >= 0.94;
        }
  
        if (this.isLandscape()) {
          // 0.887 is NOT a full screen!!!
          return value >= 0.89;
        } else {
          return value >= 0.91;
        }
      }
    }
  
    private computeUrlBarHeight() {
      return this.getScreenHeight() - window.innerHeight;
    }
  
    private show() {
      let fps = 60;
      if (!this.animation) {
        this.animation = setInterval(() => {
          this.handYOffset -= 1;
          //this.arrowYOffset -= 1;
          if (this.handYOffset < -100) {
            this.handYOffset = 0;
          }
  
          if (this.arrowYOffset < -100) {
            this.arrowYOffset = 0;
          }
  
          this.resizeIcons();
        }, (1 / fps) * 1000);
      }
  
      this.resizeIcons();
      this.viewComponent.style.display = "block";
      this.fullscreenAnimation.style.display = "block";
      document.body.style.overflow = "visible";
      this.overflowDiv.style.display = "block";
    }
  
    private hide() {
      if (this.animation) {
        clearInterval(this.animation);
        this.animation = undefined;
      }
  
      this.viewComponent.style.display = "none";
      this.fullscreenAnimation.style.display = "none";

      // Firefox does not like hidden overflow
      document.body.style.overflow = "auto";

      this.resizeHandler();
      this.overflowDiv.style.display = "none";
    }
  
    private scroll(value = undefined) {
      if (value == undefined) {
        value = 0;
      }
  
      let original = document.body.style.overflow;
      document.body.style.overflow = "visible";
      window.scrollTo({ left: 0, top: value, behavior: "auto" });
      document.body.style.paddingTop = `${value}px`;
      document.body.style.overflow = original;
    }
  
    private resizeIcons() {
      if (this.isLandscape()) {
        this.handImage.style.cssText = `position: absolute; top: calc(50% + ${
          50 + this.handYOffset
        }px); left: 55%; margin-right: -65%; transform: translate(-50%, -50%) rotate(-13deg); width: 20vw;opacity:${this.opacity}`;
        this.arrowImage.style.cssText = `position: absolute; top: calc(20% + ${
          50 + this.arrowYOffset
        }px); left: 50%; transform: translate(-50%, -50%); width: 10vw;opacity:${this.opacity}`;
  
        this.viewComponent.style.cssText = `position:absolute;top:0;left:0;display:block;background-color:rgba(0, 0, 0, 0.7);height:calc(100vh + ${0}px);width:100%`;
        this.overflowDiv.style.cssText = `height: 15vh;width:100%;position:absolute;top:calc(100vh + ${0}px);left:0;background-color:black;text-align:center;color:white`;
      } else {
        this.handImage.style.cssText = `position: absolute; top: calc(50% + ${
          50 + this.handYOffset
        }px); left: 65%; margin-right: -65%; transform: translate(-50%, -50%) rotate(-17deg); width: 50vw;opacity:${this.opacity}`;
        this.arrowImage.style.cssText = `position: absolute; top: calc(20% + ${
          50 + this.arrowYOffset
        }px); left: 50%; transform: translate(-50%, -50%); width: 20vw;opacity:${this.opacity}`;
  
        this.viewComponent.style.cssText = `position:absolute;top:0;left:0;display:block;background-color:rgba(0, 0, 0, 0.7);height:calc(100vh + ${0}px);width:100%`;
        this.overflowDiv.style.cssText = `height: 15vh;width:100%;position:absolute;top:calc(100vh + ${0}px);left:0;background-color:black;text-align:center;color:white`;
      }
    }
  
    private checkForFullscreen() {
      this.scroll();
      if (!this.isFullscreen()) {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  export default new FullscreenUtils();